import { useState, useEffect } from 'react';

/**
 * Custom hook for managing damage flash effects
 * @param {Object} player - Player object with health property
 * @param {Object} opponent - Opponent object with health property
 * @returns {Object} - Damage flash state and utilities
 */
export const useDamageFlash = (player, opponent) => {
  const [playerDamageFlash, setPlayerDamageFlash] = useState(false);
  const [opponentDamageFlash, setOpponentDamageFlash] = useState(false);
  
  // Track previous health values for comparison
  const [previousPlayerHealth, setPreviousPlayerHealth] = useState(null);
  const [previousOpponentHealth, setPreviousOpponentHealth] = useState(null);

  // Track player health changes for damage visualization
  useEffect(() => {
    if (player && previousPlayerHealth !== null && player.health < previousPlayerHealth) {
      setPlayerDamageFlash(true);
      setTimeout(() => setPlayerDamageFlash(false), 500);
    }
    setPreviousPlayerHealth(player?.health);
  }, [player?.health, previousPlayerHealth]);
  
  // Track opponent health changes for damage visualization
  useEffect(() => {
    if (opponent && previousOpponentHealth !== null && opponent.health < previousOpponentHealth) {
      setOpponentDamageFlash(true);
      setTimeout(() => setOpponentDamageFlash(false), 500);
    }
    setPreviousOpponentHealth(opponent?.health);
  }, [opponent?.health, previousOpponentHealth]);

  return {
    playerDamageFlash,
    opponentDamageFlash,
    // Utility functions
    triggerPlayerFlash: () => {
      setPlayerDamageFlash(true);
      setTimeout(() => setPlayerDamageFlash(false), 500);
    },
    triggerOpponentFlash: () => {
      setOpponentDamageFlash(true);
      setTimeout(() => setOpponentDamageFlash(false), 500);
    },
    triggerBothFlash: () => {
      setPlayerDamageFlash(true);
      setOpponentDamageFlash(true);
      setTimeout(() => {
        setPlayerDamageFlash(false);
        setOpponentDamageFlash(false);
      }, 500);
    }
  };
};

/**
 * Get damage flash styles for an element
 * @param {boolean} isFlashing - Whether the element is currently flashing
 * @param {Object} additionalStyles - Additional styles to merge
 * @returns {Object} - Combined styles object
 */
export const getDamageFlashStyles = (isFlashing, additionalStyles = {}) => {
  return {
    filter: isFlashing ? "brightness(2) saturate(2) hue-rotate(0deg)" : "none",
    transition: "filter 0.1s ease-in-out",
    animation: isFlashing ? "damageFlash 0.5s ease-in-out" : "none",
    ...additionalStyles
  };
};

/**
 * Damage flash animation keyframes (to be added to CSS)
 */
export const damageFlashKeyframes = `
@keyframes damageFlash {
  0% { filter: brightness(1) saturate(1) hue-rotate(0deg); }
  50% { filter: brightness(2) saturate(2) hue-rotate(0deg); }
  100% { filter: brightness(1) saturate(1) hue-rotate(0deg); }
}
`;
